import React, { useEffect, useState } from 'react';
import { useChatbot } from 'contexts/ChatbotContext';
import { useTranslation } from 'react-i18next';
import 'components/chatbot/Chatbot.scss';

const ChatbotSidebar: React.FC = () => {
  const { t } = useTranslation('translation');
  const {
    isSidebarOpen,
    conversations,
    currentConversationId,
    createConversation,
    selectConversation,
    archiveConversation,
    unarchiveConversation,
    renameConversation,
    toggleSidebar,
  } = useChatbot();

  const [editingId, setEditingId] = useState<string | null>(null);
  const [editTitle, setEditTitle] = useState('');

  useEffect(() => {
    const handleEscapeKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && isSidebarOpen) {
        toggleSidebar();
      }
    };

    document.addEventListener('keydown', handleEscapeKey);

    return () => {
      document.removeEventListener('keydown', handleEscapeKey);
    };
  }, [isSidebarOpen, toggleSidebar]);

  if (!isSidebarOpen) return null;

  const formatDate = (date: Date): string => {
    // Compare calendar days, not 24h slices: 23:00 yesterday is "yesterday" at 01:00
    const startOfDay = (d: Date) => new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
    const days = Math.round((startOfDay(new Date()) - startOfDay(date)) / (1000 * 60 * 60 * 24));

    if (days <= 0) return t('chatbot.today');
    if (days === 1) return t('chatbot.yesterday');
    if (days < 7) return t('chatbot.daysago', { count: days });
    return date.toLocaleDateString();
  };

//   const handleRename = (id: string, currentTitle: string) => {
//     setEditingId(id);
//     setEditTitle(currentTitle);
//   };

  const handleSaveRename = async (id: string) => {
    if (editTitle.trim() && editTitle !== conversations.find(c => c.id === id)?.title) {
      await renameConversation(id, editTitle.trim());
    }
    setEditingId(null);
    setEditTitle('');
  };

  const handleCancelRename = () => {
    setEditingId(null);
    setEditTitle('');
  };

  return (
    <>
      <div className="chatbot-sidebar-overlay" onClick={toggleSidebar} />
      <div className="chatbot-sidebar">
        <div className="chatbot-sidebar-header">
          <h3>{t('chatbot.conversations')}</h3>
          <button className="chatbot-sidebar-close" onClick={toggleSidebar} aria-label={t('chatbot.closesidebar')}>
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
              <path
                d="M15 5L5 15M5 5L15 15"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
              />
            </svg>
          </button>
        </div>

        <button className="chatbot-sidebar-new-btn" onClick={createConversation}>
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
            <path
              d="M8 1V15M1 8H15"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
            />
          </svg>
          {t('chatbot.newconversation')}
        </button>

        <div className="chatbot-sidebar-list">
          {conversations.filter(c => !c.isArchived).length === 0 ? (
            <div className="chatbot-sidebar-empty">{t('chatbot.noconversations')}</div>
          ) : (
            conversations.filter(c => !c.isArchived).map((conversation) => (
              <div
                key={conversation.id}
                className={`chatbot-sidebar-item ${
                  conversation.id === currentConversationId ? 'active' : ''
                }`}
                onClick={() => !editingId && selectConversation(conversation.id)}
              >
                <div className="chatbot-sidebar-item-content">
                  {editingId === conversation.id ? (
                    <div className="chatbot-sidebar-item-edit">
                      <input
                        type="text"
                        value={editTitle}
                        onChange={(e) => setEditTitle(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') handleSaveRename(conversation.id);
                          if (e.key === 'Escape') handleCancelRename();
                        }}
                        autoFocus
                        onClick={(e) => e.stopPropagation()}
                      />
                      <div className="chatbot-sidebar-item-edit-actions">
                        <button onClick={() => handleSaveRename(conversation.id)}>✓</button>
                        <button onClick={handleCancelRename}>✕</button>
                      </div>
                    </div>
                  ) : (
                    <>
                      <div className="chatbot-sidebar-item-title">
                        {conversation.title}
                        {conversation.isLoadingHistory && <span className="loading-indicator">...</span>}
                      </div>
                      {conversation.updatedAt && (
                        <div className="chatbot-sidebar-item-date">
                          {formatDate(conversation.updatedAt)}
                        </div>
                      )}
                    </>
                  )}
                </div>
                {!editingId && !!conversation?.externalId && (
                  <button
                    className="chatbot-sidebar-item-archive"
                    onClick={(e) => {
                      e.stopPropagation();
                      if (conversation.isArchived) {
                        unarchiveConversation(conversation.id);
                      } else {
                        archiveConversation(conversation.id);
                      }
                    }}
                    aria-label={conversation.isArchived ? t('chatbot.unarchive') : t('chatbot.archive')}
                    title={conversation.isArchived ? t('chatbot.unarchive') : t('chatbot.archive')}
                  >
                    <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                      <path
                        d="M2 4H14M3 4V13H13V4M6 7V10M10 7V10"
                        stroke="currentColor"
                        strokeWidth="1.5"
                        strokeLinecap="round"
                      />
                    </svg>
                  </button>
                )}
              </div>
            ))
          )}
        </div>
      </div>
    </>
  );
};

export default ChatbotSidebar;
